import React, { useState } from "react";
import Image from "next/image";

import { addMembershipById } from "lib/api";
import { useOnRemoveBasketItem } from "components/BasketLists/BasketList.utils";
import { withErrorBoundary } from "react-error-boundary";
import { datadogLogs } from "@datadog/browser-logs";

import { MembershipToggleButton } from "./MembershipToggleButton";
import {
  membershipToggleCardStyles,
  savingsContainer,
  nonBannerContainer,
  container,
  cardImageContainer,
  textContainer,
  textWrapper,
  titleText,
  priceWrapper,
  accordion,
  accordionHeaderStyles,
  accordionContent,
  allLinesStyles,
  toggleButton,
  bannerContainer,
  membershipPriceText,
  firstLineOnlyStyles
} from "./MembershipToggleCard.css";

import { setShowAccessPassPopup } from "../../PDP/Pricing/utils";
import { useGetMembershipToggleCardData } from "./hooks/useGetMembershipToggleCardData";
import { trackClicked } from "tracking/utils/trackClicked";
import type { TrackingClicked } from "@shared/graphql.types";

export const MembershipToggleCardComponent: React.FC = () => {
  const {
    heading,
    isToggled,
    showToggle,
    accordionHeading,
    accordionDescription,
    termsBanner,
    image,
    membershipPrice,
    membershipId,
    membershipInBag,
    openAccordionTracking,
    onToggleTracking,
    savingsText
  } = useGetMembershipToggleCardData();

  const [toggleAccordion, setToggleAccordion] = useState(false);
  const [loading, setLoading] = useState(false);
  const { onRemoveBasketItem } = useOnRemoveBasketItem("membershipToggleCard");

  const onToggleCallback = async () => {
    setLoading(true);

    if (onToggleTracking) trackClicked([onToggleTracking]);

    if (!isToggled && membershipId) {
      try {
        await addMembershipById(
          membershipId,
          undefined,
          "MEMBERSHIP_TOGGLE_CARD"
        );

        // TODO: This logic should be moved to bp-api and cookie set with context.res.cookie("bp-typical-journey", "0")
        setShowAccessPassPopup("0");
      } catch (err) {
        console.error(
          `Could not add membership: ${membershipId} to cache in MembershipToggleCard`,
          err
        );
      }
    }

    if (isToggled && membershipInBag) {
      try {
        await onRemoveBasketItem(membershipInBag);
        // TODO: This logic should be moved to bp-api and cookie set with context.res.cookie("bp-typical-journey", "0")
        setShowAccessPassPopup("1");
      } catch (err) {
        console.error(
          `Could not remove membership from bag in MembershipToggleCard`,
          err
        );
      }
    }

    setLoading(false);
  };

  const onToggleAccordion = () => {
    if (openAccordionTracking) {
      trackClicked([
        {
          ...openAccordionTracking,
          properties: {
            ...openAccordionTracking?.properties,
            module_content_details: !toggleAccordion
              ? "Open description"
              : "Close description"
          }
        } as TrackingClicked
      ]);
    }

    setToggleAccordion(!toggleAccordion);
  };

  if (!showToggle) {
    return null;
  }
  return (
    <div
      className={membershipToggleCardStyles}
      data-testid="membership-toggle-card"
    >
      <div className={savingsContainer}>{savingsText}</div>
      <div className={nonBannerContainer}>
        <div className={container}>
          {image && (
            <div className={cardImageContainer}>
              <Image
                src={image.src || "/images/placeholders/1440x595.svg"}
                alt={image.description || "Membership toggle card image"}
                fill
                sizes="(max-width: 768px) 5.6rem, (max-width: 1200px) 6rem"
                style={{
                  objectFit: "contain"
                }}
              />
            </div>
          )}

          <div className={textContainer}>
            <div className={textWrapper}>
              <p>
                <span className={titleText}>{heading}</span>
                <span className={membershipPriceText}>{membershipPrice}</span>
              </p>
            </div>
          </div>
          <div className={priceWrapper}>
            <MembershipToggleButton
              aria-controls="membership-accordion"
              toggled={!!isToggled}
              onToggleCallback={onToggleCallback}
              loading={loading}
            />
          </div>
        </div>
        <div
          className={accordion}
          id="membership-accordion"
          aria-expanded={toggleAccordion}
        >
          <div className={accordionHeaderStyles}>
            <p>{accordionHeading}</p>
          </div>

          <div
            className={accordionContent}
            data-testid="within-accordion-content"
          >
            {accordionDescription && (
              <div
                id="membership-perks"
                className={
                  toggleAccordion ? allLinesStyles : firstLineOnlyStyles
                }
                dangerouslySetInnerHTML={{
                  __html: accordionDescription
                }}
              />
            )}
          </div>

          <p className={toggleButton} onClick={onToggleAccordion}>
            {!toggleAccordion ? "See more" : " See less"}
          </p>
        </div>
      </div>
      {termsBanner && (
        <div
          id="terms-and-conditions-banner"
          className={bannerContainer}
          dangerouslySetInnerHTML={{
            __html: termsBanner
          }}
        />
      )}
    </div>
  );
};

export const MembershipToggleCard = withErrorBoundary(
  MembershipToggleCardComponent,
  {
    fallback: <div />,
    onError(error) {
      datadogLogs.logger.error(
        "Failed to load 'Membership Toggle Card' on the basket page",
        { error }
      );
    }
  }
);
